import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../client';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';

export interface OrderSearchResult {
  id: string;
  orderNumber: string;
  customerName: string;
  status: string;
  deepLink: string;
  warehouseId: string | null;
  warehouseName: string | null;
}

export interface ItemSearchResult {
  id: string;
  sku: string;
  name: string;
  type: string;
  deepLink: string;
}

export interface SearchResults {
  orders: OrderSearchResult[];
  items: ItemSearchResult[];
  total: {
    orders: number;
    items: number;
  };
}

export function useSearch(
  query: string,
  opts: {
    type?: 'all' | 'orders' | 'items';
    debounceMs?: number;
  } = {},
) {
  const debouncedQuery = useDebouncedValue(query, opts.debounceMs ?? 300);

  return useQuery<SearchResults>({
    queryKey: ['search', debouncedQuery, opts.type],
    queryFn: () =>
      apiClient
        .get('/api/search', {
          params: {
            q: debouncedQuery,
            ...(opts.type && opts.type !== 'all' ? { type: opts.type } : {}),
          },
        })
        .then((r) => r.data as SearchResults),
    enabled: debouncedQuery.length >= 2,
    staleTime: 30_000,
  });
}
